import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $profileFeatureStore, $userAccountsStore, $userStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { UserType } from '@shared/api';
import { Accounts, AccountsAndCards, UserSidebar } from './widgets';
import { PageContainer, UserInfo, UserInfoHeader, View } from '@widgets';

export const UserPage = () => {
  const { id } = useParams<{ id: string }>();
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const userStore = useUnit($userStore.store);

  const { t } = useTranslation('user');

  useEffect(() => {
    if (!profileFeatureStore.data?.userManagement?.viewUserProfileDetails) navigate(RoutePaths.users.onNavigate());
  }, [profileFeatureStore.data?.userManagement?.viewUserProfileDetails]);

  useEffect(() => {
    if (!id) return;
    Promise.allSettled([$userStore.events.getUser({ id }), $userAccountsStore.events.getUserAccounts({ AssignedUserId: id })]);
    return () => {
      $userStore.events.clear();
      $userAccountsStore.events.clear();
    };
  }, [id]);

  useEffect(() => {
    if (userStore.loading) return;
    if (userStore.error !== 'Action is forbidden.') return;
    $userStore.events.clear();
    navigate(RoutePaths.error.onNavigate(), { replace: true });
  }, [userStore.loading, userStore.error]);

  const accessAccounts = useMemo(
    () => userStore.data?.userType === UserType.Manager && profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions,
    [profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions, userStore.data?.userType],
  );
  const accessCards = useMemo(
    () => userStore.data?.userType === UserType.User && profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions,
    [profileFeatureStore.data?.userManagement?.managerAccountsOrCardsPermissions, userStore.data?.userType],
  );
  return (
    <PageContainer title={t('title')} back backText={t('backText')} return>
      <View
        loading={userStore.loading}
        error={userStore.error}
        noData={!userStore.data || !profileFeatureStore.data?.userManagement?.viewUserProfileDetails}
        onRefresh={() => $userStore.events.getUser({ id: id! })}
      >
        <div className={cn('flex justify-center', PaletteUtils.isVcfcPalette && 'max-md:block')}>
          {userStore.data && profileFeatureStore.data?.userManagement?.viewUserProfileDetails && (
            <>
              <UserInfo value={userStore.data}>
                <UserInfoHeader className='mb-12' user={userStore.data} />
                {userStore.data.userType !== UserType.Admin && (
                  <>
                    {accessAccounts && <Accounts user={userStore.data} />}
                    {accessCards && <AccountsAndCards user={userStore.data} />}
                  </>
                )}
              </UserInfo>
              <UserSidebar className={cn(PaletteUtils.isVcfcPalette && 'max-md:mt-8')} user={userStore.data} />
            </>
          )}
        </div>
      </View>
    </PageContainer>
  );
};
